import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  /** A way back — the state is not a dead end (§8). */
  action?: ReactNode;
  /**
   * The sentence, when the refusal is not about a candidate. It defaults to the copy §8 settled for
   * the candidate surface, which is the case the design fixes; a screen outside that surface passes
   * its own, exactly as `EmptyState` does.
   */
  message?: string;
}

/**
 * State 4 of `DESIGN.md` §8, and the copy is settled rather than a placeholder.
 *
 * **The API answers 404, not 403** (backend TASK-029, 2026-07-24): an interviewer reaching past
 * their assignment inside their own tenant gets the same `404 {"detail": "Not found."}` as for
 * another tenant's id and for an id that never existed, because a 403 would confirm that a
 * candidate with that id exists to someone with no business knowing. The counter-argument — that
 * a 404 is worse for a mistyped URL — is answered here: **render this state on a 404 from a
 * candidate route**, not an error state, so both cases read the same and neither leaks.
 */
export function ForbiddenState({ action, message }: Props) {
  const { t } = useTranslation('components');

  return (
    <div className="flex flex-col items-center gap-3 rounded border border-rule bg-paper px-8 py-12 text-center">
      <Lock size={20} strokeWidth={1.5} className="text-ink-500" aria-hidden />
      <p className="text-sm text-ink-900">{message ?? t('state.forbidden')}</p>
      {action}
    </div>
  );
}
